import * as React from "react";

import { getApiErrorMessage } from "api/apiError";
import { Alert, Button, FormField, Textarea } from "Components/ui";
import DialogShell from "./DialogShell";

const MAX_REASON_LENGTH = 500;

export interface ConfirmDialogProps {
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  /** Approve-type actions need a written reason (stored in the audit log and timeline). */
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Extra inputs (dates, numbers) rendered above the reason. */
  children?: React.ReactNode;
  /** Validation message for the extra inputs; blocks the confirm button. */
  blockingError?: string | null;
  /** Show API errors inside the dialog (use with a mutation that does not toast errors). */
  showErrorInline?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Confirmation dialog for billing actions. Approve-type actions pass `requireReason`; destructive
 * ones pass `tone="danger"` for a red confirm button. Closes itself once `onConfirm` resolves.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  description,
  confirmLabel,
  tone = "primary",
  requireReason = false,
  reasonLabel = "Reason",
  reasonPlaceholder = "Why is this needed? Saved in the audit log and the client timeline.",
  children,
  blockingError = null,
  showErrorInline = false,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = React.useState<string>("");
  const [touched, setTouched] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const mounted = React.useRef<boolean>(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const trimmed = reason.trim();
  const reasonError =
    requireReason && touched && !trimmed
      ? "A reason is required."
      : trimmed.length > MAX_REASON_LENGTH
      ? `Keep it under ${MAX_REASON_LENGTH} characters.`
      : null;
  const canConfirm =
    !submitting &&
    !blockingError &&
    (!requireReason || Boolean(trimmed)) &&
    trimmed.length <= MAX_REASON_LENGTH;

  const handleConfirm = async () => {
    setTouched(true);
    if (!canConfirm) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      onClose();
    } catch (err) {
      if (mounted.current) {
        setSubmitting(false);
        if (showErrorInline) {
          setError(getApiErrorMessage(err));
        }
      }
    }
  };

  return (
    <DialogShell
      title={title}
      onClose={submitting ? () => undefined : onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={handleConfirm}
            loading={submitting}
            disabled={!canConfirm && touched}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {description && <div className="text-sm text-muted-foreground">{description}</div>}
        {children}
        {blockingError && touched && (
          <p role="alert" className="text-xs text-danger-text">
            {blockingError}
          </p>
        )}
        {requireReason && (
          <FormField
            label={reasonLabel}
            required
            error={reasonError || undefined}
            hint="Required for approval actions."
          >
            <Textarea
              rows={3}
              value={reason}
              maxLength={MAX_REASON_LENGTH + 50}
              onChange={(event) => setReason(event.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={reasonPlaceholder}
            />
          </FormField>
        )}
        {error && (
          <Alert tone="danger" title="Not done">
            {error}
          </Alert>
        )}
      </div>
    </DialogShell>
  );
};

export default ConfirmDialog;
